import { MM_TASK_TYPES, MmOutcome, MmReviewStatus, MmTaskStatus, MmTaskType } from '../types/enums';

export type MmBadgeTone =
  | 'neutral'
  | 'info'
  | 'process'
  | 'success'
  | 'warning'
  | 'attention'
  | 'danger'
  | 'special'
  | 'taskTypeFree'
  | 'taskTypeTpc'
  | 'taskTypeContinuous'
  | 'taskTypeKvi';

export type MmStatusGroup = 'active' | 'archived';

export interface MmBadgeSpec {
  tone: MmBadgeTone;
  labelKey: string;
  hintKey: string | null;
  order: number;
  group: MmStatusGroup | null;
  icon?: string;
}

const TASK_STATUS_MAP: { [key: string]: MmBadgeSpec } = {
  [MmTaskStatus.ASSIGNED]: {
    tone: 'info',
    labelKey: 'priceMonitoring.mobileMonitoring.taskStatus.assigned',
    hintKey: null,
    order: 10,
    group: 'active',
  },
  [MmTaskStatus.COMPLETED]: {
    tone: 'warning',
    labelKey: 'priceMonitoring.mobileMonitoring.taskStatus.completed',
    hintKey: 'priceMonitoring.mobileMonitoring.taskStatus.completedHint',
    order: 20,
    group: 'active',
  },
  [MmTaskStatus.ACCEPTED]: {
    tone: 'success',
    labelKey: 'priceMonitoring.mobileMonitoring.taskStatus.accepted',
    hintKey: null,
    order: 30,
    group: 'archived',
  },
  [MmTaskStatus.REJECTED]: {
    tone: 'danger',
    labelKey: 'priceMonitoring.mobileMonitoring.taskStatus.rejected',
    hintKey: null,
    order: 40,
    group: 'archived',
  },
};

const REVIEW_STATUS_MAP: { [key: string]: MmBadgeSpec } = {
  [MmReviewStatus.PENDING]: {
    tone: 'neutral',
    labelKey: 'priceMonitoring.mobileMonitoring.reviewStatus.pending',
    hintKey: null,
    order: 10,
    group: null,
  },
  [MmReviewStatus.ACCEPTED]: {
    tone: 'success',
    labelKey: 'priceMonitoring.mobileMonitoring.reviewStatus.accepted',
    hintKey: null,
    order: 20,
    group: null,
  },
  [MmReviewStatus.REJECTED]: {
    tone: 'danger',
    labelKey: 'priceMonitoring.mobileMonitoring.reviewStatus.rejected',
    hintKey: null,
    order: 30,
    group: null,
  },
  [MmReviewStatus.PRICE_CORRECTED]: {
    tone: 'info',
    labelKey: 'priceMonitoring.mobileMonitoring.reviewStatus.priceCorrected',
    hintKey: 'priceMonitoring.mobileMonitoring.reviewStatus.priceCorrectedHint',
    order: 40,
    group: null,
  },
};

const OUTCOME_MAP: { [key: string]: MmBadgeSpec } = {
  [MmOutcome.SKIPPED]: {
    tone: 'attention',
    labelKey: 'priceMonitoring.mobileMonitoring.outcome.skipped',
    hintKey: null,
    order: 10,
    group: null,
    icon: '⊘',
  },
  other: {
    tone: 'attention',
    labelKey: 'priceMonitoring.mobileMonitoring.outcome.other',
    hintKey: null,
    order: 12,
    group: null,
    icon: '⊘',
  },
  [MmOutcome.PRODUCT_NOT_FOUND]: {
    tone: 'danger',
    labelKey: 'priceMonitoring.mobileMonitoring.outcome.productNotFound',
    hintKey: null,
    order: 20,
    group: null,
    icon: '✖',
  },
  [MmOutcome.OUT_OF_STOCK]: {
    tone: 'attention',
    labelKey: 'priceMonitoring.mobileMonitoring.outcome.outOfStock',
    hintKey: null,
    order: 25,
    group: null,
    icon: '⊗',
  },
  [MmOutcome.ANALOG_COMPARE_FAILED]: {
    tone: 'special',
    labelKey: 'priceMonitoring.mobileMonitoring.outcome.analogCompareFailed',
    hintKey: 'priceMonitoring.mobileMonitoring.outcome.analogCompareFailedHint',
    order: 30,
    group: null,
    icon: '✱',
  },
  [MmOutcome.PRICE_NOT_RECOGNIZED]: {
    tone: 'warning',
    labelKey: 'priceMonitoring.mobileMonitoring.outcome.priceNotRecognized',
    hintKey: null,
    order: 40,
    group: null,
    icon: '▲',
  },
  [MmOutcome.NO_PRICE_TAG]: {
    tone: 'warning',
    labelKey: 'priceMonitoring.mobileMonitoring.outcome.noPriceTag',
    hintKey: null,
    order: 45,
    group: null,
    icon: '▽',
  },
  [MmOutcome.PRICE_CAPTURED]: {
    tone: 'success',
    labelKey: 'priceMonitoring.mobileMonitoring.outcome.priceCaptured',
    hintKey: null,
    order: 50,
    group: null,
    icon: '●',
  },
  [MmOutcome.NOT_DONE]: {
    tone: 'neutral',
    labelKey: 'priceMonitoring.mobileMonitoring.outcome.notDone',
    hintKey: null,
    order: 60,
    group: null,
    icon: '○',
  },
  [MmOutcome.UNSPECIFIED]: {
    tone: 'warning',
    labelKey: 'priceMonitoring.mobileMonitoring.outcome.unspecified',
    hintKey: 'priceMonitoring.mobileMonitoring.outcome.unspecifiedHint',
    order: 70,
    group: null,
    icon: '⚠',
  },
};

/** Порядок берётся из MM_TASK_TYPES: второго реестра не заводить, иначе списки разъедутся */
function taskTypeOrder(type: MmTaskType): number {
  const index = MM_TASK_TYPES.indexOf(type);
  const position = index < 0 ? MM_TASK_TYPES.length : index;

  return (position + 1) * 10;
}

/** В карте все четыре типа: MM_KVI_SELECTABLE скрывает выбор только на форме создания */
const TASK_TYPE_MAP: { [key: string]: MmBadgeSpec } = {
  [MmTaskType.FREE]: {
    tone: 'taskTypeFree',
    labelKey: 'priceMonitoring.mobileMonitoring.taskTypes.free',
    hintKey: null,
    order: taskTypeOrder(MmTaskType.FREE),
    group: null,
  },
  [MmTaskType.TPC]: {
    tone: 'taskTypeTpc',
    labelKey: 'priceMonitoring.mobileMonitoring.taskTypes.tpc',
    hintKey: null,
    order: taskTypeOrder(MmTaskType.TPC),
    group: null,
  },
  [MmTaskType.CONTINUOUS]: {
    tone: 'taskTypeContinuous',
    labelKey: 'priceMonitoring.mobileMonitoring.taskTypes.continuous',
    hintKey: null,
    order: taskTypeOrder(MmTaskType.CONTINUOUS),
    group: null,
  },
  [MmTaskType.KVI]: {
    tone: 'taskTypeKvi',
    labelKey: 'priceMonitoring.mobileMonitoring.taskTypes.kvi',
    hintKey: null,
    order: taskTypeOrder(MmTaskType.KVI),
    group: null,
  },
};

const PRICE_SOURCE_MAP: { [key: string]: MmBadgeSpec } = {
  ocr: {
    tone: 'info',
    labelKey: 'priceMonitoring.mobileMonitoring.priceSource.ocr',
    hintKey: 'priceMonitoring.mobileMonitoring.priceSource.ocrHint',
    order: 10,
    group: null,
  },
  manual: {
    tone: 'neutral',
    labelKey: 'priceMonitoring.mobileMonitoring.priceSource.manual',
    hintKey: 'priceMonitoring.mobileMonitoring.priceSource.manualHint',
    order: 20,
    group: null,
  },
};

export type MmBadgeDomain = 'taskStatus' | 'reviewStatus' | 'outcome' | 'taskType' | 'priceSource';

const DOMAIN_MAPS: { [key: string]: { [key: string]: MmBadgeSpec } } = {
  taskStatus: TASK_STATUS_MAP,
  reviewStatus: REVIEW_STATUS_MAP,
  outcome: OUTCOME_MAP,
  taskType: TASK_TYPE_MAP,
  priceSource: PRICE_SOURCE_MAP,
};

export function getBadgeSpec(domain: MmBadgeDomain, value: string | null | undefined): MmBadgeSpec | null {
  if (!value) {
    return null;
  }

  const map = DOMAIN_MAPS[domain];

  if (!map) {
    return null;
  }

  const spec = map[value];

  if (spec) {
    return spec;
  }

  const lower = value.toLowerCase();

  return lower !== value ? (map[lower] ? map[lower] : null) : null;
}

export interface MmBadgeEntry {
  code: string;
  spec: MmBadgeSpec;
}

export function listDomainEntries(domain: MmBadgeDomain): MmBadgeEntry[] {
  const map = DOMAIN_MAPS[domain];

  if (!map) {
    return [];
  }

  const codes = Object.keys(map);
  const entries: MmBadgeEntry[] = [];

  for (let i = 0; i < codes.length; i += 1) {
    entries.push({ code: codes[i], spec: map[codes[i]] });
  }

  entries.sort(function compareByOrder(a: MmBadgeEntry, b: MmBadgeEntry): number {
    return a.spec.order - b.spec.order;
  });

  return entries;
}

export function listTaskStatusesByGroup(group: MmStatusGroup): string[] {
  const entries = listDomainEntries('taskStatus');
  const codes: string[] = [];

  for (let i = 0; i < entries.length; i += 1) {
    if (entries[i].spec.group === group) {
      codes.push(entries[i].code);
    }
  }

  return codes;
}

export function skipReasonKey(code: string): string {
  return 'priceMonitoring.mobileMonitoring.skipReason.' + code;
}

export function outcomeKey(code: string): string | null {
  const spec = OUTCOME_MAP[code];

  return spec ? spec.labelKey : null;
}
